/* eslint-disable @typescript-eslint/ban-ts-comment */
import NextLink from 'next/link';
import React, { ComponentPropsWithoutRef } from 'react';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native-web';

import {
  getUrlWithOverridenQuery,
  makeParametricUrl,
} from '../../../helpers/url';
import { LinkOptions, To } from '../types';
import { convertLocationToHref } from '../utils';

export type LinkProps = Omit<
  ComponentPropsWithoutRef<typeof NextLink>,
  'href' | 'style'
> &
  LinkOptions & {
    params?: Record<string, string>;
    queries?: Record<string, string>;
    to: To;
    style?: StyleProp<ViewStyle>;
    children: React.ReactNode;
  };

const Link: React.FC<LinkProps> = (props: LinkProps) => {
  const {
    to,
    isUsingAnchor,
    shouldOpenNewTab,
    style,
    children,
    params,
    queries,
    passHref = false,
    ...rest
  } = props;

  const href = getUrlWithOverridenQuery(
    makeParametricUrl(convertLocationToHref(to), params || {}),
    queries || {},
  );

  const target = shouldOpenNewTab ? '_blank' : undefined;
  const [className, inlineStyle] = StyleSheet([s.view, style]);

  if (isUsingAnchor || shouldOpenNewTab) {
    return (
      <a
        href={href}
        target={target}
        rel="noopener noreferrer"
        {...rest}
        className={className}
        style={inlineStyle}
      >
        {children}
      </a>
    );
  }

  /**
   * notes related to legacy behavior: https://github.com/vercel/next.js/commit/489e65ed98544e69b0afd7e0cfc3f9f6c2b803b7
   */
  return (
    <NextLink href={href} passHref={passHref}>
      {!passHref ? (
        <div style={inlineStyle} className={className}>
          {children}
        </div>
      ) : (
        children
      )}
    </NextLink>
  );
};

const s = StyleSheet.create({
  view: {
    alignItems: 'stretch',
    borderWidth: 0,
    borderColor: 'black',
    borderStyle: 'solid',
    boxSizing: 'border-box',
    display: 'flex',
    flexBasis: 'auto',
    flexDirection: 'column',
    flexShrink: 0,
    margin: 0,
    minHeight: 0,
    minWidth: 0,
    padding: 0,
    position: 'relative',
    zIndex: 0,
  },
});

export default Link;
